<template>
  <div :class="getElementClasses">
    <div
      ref="otpInputDigits"
      class="otp-input__digits"
    >
      <input
        v-model="digits[index]"
        v-for="(digit, index) in digits"
        :key="digit + index"
        type="text"
        inputmode="numeric"
        maxlength="1"
        autocomplete="one-time-code"
        class="otp-input__digit"
        @input="handleInput($event, index)"
        @keydown="handleKeyDown($event, index)"
        @paste.prevent="handlePaste"
      >
    </div>

    <transition name="fade">
      <small
        v-if="hasError"
        class="otp-input__error-message"
      >
        {{ errorMessage }}
      </small>
    </transition>
  </div>
</template>

<script>
// UTILS
import {onlyDigits} from "commons/js/RegExp";

// CONSTANTS
const KEYS_ENUM = {
  ARROW_RIGHT: "ArrowRight",
  ARROW_LEFT: "ArrowLeft",
  TAB: "Tab",
  BACKSPACE: "Backspace",
  DELETE: "Delete",
  CONTROL: "Control",
  V_UPPER: "V",
  V_LOWER: "v"
}

const ACCEPTED_KEYS = Object.values(KEYS_ENUM);

const CUSTOM_KEYS = [
  KEYS_ENUM.ARROW_RIGHT,
  KEYS_ENUM.ARROW_LEFT,
  KEYS_ENUM.BACKSPACE,
  KEYS_ENUM.DELETE
];

export default {
  name: "OTPInput",

  data() {
    return {
      digits: [],
      currentActiveField: 0
    }
  },

  props: {
    /**
     * Sets an initial value for the OTP field.
     */
    initialValue: {
      type: String,
      default: ''
    },

    /**
     * Defines the amount of digits the input should have.
     */
    digitsCount: {
      type: Number,
      required: true
    },

    /**
     * Indicates whether some validation has failed or not.
     */
    hasError: {
      type: Boolean,
      default: false
    },

    /**
     * The error message to be shown below the input.
     */
    errorMessage: {
      type: String,
      default: 'Código inválido'
    }
  },

  created() {
    this.setInitialDigits();
  },

  mounted() {
    this.setFocusByIndex(0);
  },

  updated() {
    if (this.isEmpty) {
      this.setFocusByIndex(0);

      return;
    }

    this.setFocusByIndex(this.currentActiveField);
  },

  computed: {
    /**
     * It binds classes on the input.
     *
     * @returns {array} An array of classes.
     */
    getElementClasses() {
      return [
        'otp-input',
        {
          'otp-input--error': this.hasError
        }
      ]
    },

    /**
     * Gets the maximum index based on the given digitsCount.
     *
     * @returns {number}
     */
    maxIndex() {
      return this.digitsCount - 1;
    },

    /**
     * Checks whether the OTP is totally empty or not.
     *
     * @returns {boolean}
     */
    isEmpty() {
      return this.digits.every(digit => !digit);
    },

    /**
     * Checks whether the OTP is totally full or not.
     *
     * @returns {boolean}
     */
    isFull() {
      return this.digits.every(digit => !!digit);
    },

    /**
     * Gets the raw value for the digits by joining them into a string.
     *
     * @returns {string}
     */
    rawValue() {
      return this.digits.join('');
    }
  },

  watch: {
    /**
     * Whenever the sequence of digits changes and the raw value is full-filled,
     * we need to inform the parent component about it.
     */
    digits: {
      deep: true,
      handler(newValue) {
        if (newValue && this.isFull) {
          this.$emit('full-filled', {code: this.rawValue});
        }
      }
    },

    /**
     * When an error occurs, it should reset the OTP field.
     *
     * @returns {void}
     */
    hasError(newValue) {
      if (newValue) {
        this.resetDigitsValue();
      }
    }
  },

  methods: {
    /**
     * Checks if the given value can be accepted as a digit.
     *
     * @param {string} value The given value to check.
     * @returns {boolean}
     */
    hasOnlyDigits(value) {
      return onlyDigits().test(value);
    },

    /**
     * It handles the focus on the properly digit by its index.
     *
     * @param {number} index The digit index.
     * @returns {void}
     */
    setFocusByIndex(index) {
      this.$refs.otpInputDigits.children[index].focus();
    },

    /**
     * It sets the input inner selection by the given index.
     *
     * @param {number} index The digit index.
     * @returns {void}
     */
    setSelectByIndex(index) {
      if (!this.digits[index]) {
        return;
      }

      this.$refs.otpInputDigits.children[index].select();
    },

    /**
     * It sets the current active OTP digit.
     *
     * @param {number} index The digit index.
     * @returns {void}
     */
    setActiveField(index) {
      this.currentActiveField = index;
    },

    /**
     * Resets the digits value.
     *
     * @returns {void}
     */
    resetDigitsValue() {
      this.setDigitsValue((index) => {
        this.digits.splice(index, 1, null);
      });
    },

    /**
     * It loops over all digits in order to handle
     * some logic to set its value.
     *
     * @param {function} callbackFn The given callback function.
     * @returns {void}
     */
    setDigitsValue(callbackFn) {
      for (let index = 0; index < this.digitsCount; index++) {
        callbackFn(index);
      };
    },

    /**
     * It sets the initial digits value.
     *
     * @returns {void}
     */
    setInitialDigits() {
      if (this.initialValue && (this.initialValue.length === this.digitsCount)) {
        this.setDigitsValue((index) => {
          this.digits.splice(index, 1, this.initialValue.charAt(index));
        });
      } else {
        this.resetDigitsValue();
      }
    },

    /**
     * It updates the digit field state by setting it's focus, select
     * and updating the current active index.
     *
     * @param {number} index The digit index.
     * @param {boolean} shouldActivate Flag to indicates when it's necessary to set the active index.
     * @returns {void}
     */
    handleFieldState(index, shouldActivate = false) {
      if (shouldActivate) {
        this.setActiveField(index);
      }

      this.setFocusByIndex(index);
      this.setSelectByIndex(index);
    },

    /**
     * It handles the user input and updates OTP digits.
     *
     * @param {InputEvent} event The input event.
     * @param {number} index The digit index.
     * @returns {void}
     */
    handleInput(event, index) {
      const {value} = event.target;

      if (!value) {
        event.preventDefault();

        return;
      }

      const rawValue = value.trim();

      const next = index + 1;

      if (rawValue.length > 1 && (rawValue.length === this.digitsCount)) {
        event.clipboardData = {
          getData: () => value
        };

        this.handlePaste(event);

        return;
      }

      this.digits.splice(index, 1, value);

      if (index < this.maxIndex) {
        this.handleFieldState(next, true);
      } else {
        this.setActiveField(index);
      }
    },

    /**
     * It handles the keydown event by preventing some defaults and customizing
     * another behaviors.
     *
     * @param {KeyboardEvent} event The keyboard event.
     * @param {number} index The digit index.
     * @returns {void}
     */
    handleKeyDown(event, index) {
      if (!this.hasOnlyDigits(event.key) && !ACCEPTED_KEYS.includes(event.key)) {
        event.preventDefault();

        return;
      }

      const next = index + 1;

      const previous = index - 1;

      if (CUSTOM_KEYS.includes(event.key)) {
        event.preventDefault();
      }

      if (event.key === KEYS_ENUM.ARROW_RIGHT && (index < this.maxIndex)) {
        this.handleFieldState(next);
      }

      if (event.key === KEYS_ENUM.ARROW_LEFT && (index > 0)) {
        this.handleFieldState(previous);
      }

      if (event.key === KEYS_ENUM.BACKSPACE || event.key === KEYS_ENUM.DELETE) {
        this.digits.splice(index, 1, null);

        if (index > 0) {
          this.handleFieldState(previous, true);
        } else {
          this.setActiveField(index);
        }

        if (this.isEmpty) {
          this.$emit('full-empty');
        }
      }
    },

    /**
     * It handles user paste events.
     * Should not accept alphanumeric characters.
     *
     * @param {ClipboardEvent} event The paste event.
     * @returns {void}
     */
    handlePaste(event) {
      const value = (event.clipboardData || window.clipboardData).getData("text/plain").trim();

      const digits = [...value];

      const hasAlphaNumericChars = digits.some(digit => !this.hasOnlyDigits(digit));

      if (hasAlphaNumericChars) {
        return;
      }

      this.setDigitsValue((index) => {
        this.digits.splice(index, 1, (digits[index] || null));
      });
    }
  }
}
</script>

<style lang="stylus" scoped>
.otp-input
  width 100%
  height auto
  min-height 80px

  &--error

    & ^[0]__digit
      color #E7505A
      border-color #E7505A

      &:focus
        box-shadow 0 0 0 2px #E7505A

  &__digits
    width 100%
    display flex
    align-items center
    justify-content center
    gap 8px

  &__digit
    display block
    width 45px
    height 60px
    border 1px solid #3598DC
    border-radius 4px
    padding 12px
    margin 0
    font-size 24px
    line-height 24px
    font-weight 700
    text-align center
    color #000000
    box-sizing border-box
    caret-color transparent

    &:focus
      box-shadow 0 0 0 2px #3598DC
      transition box-shadow .3s ease-in
      -webkit-box-shadow 0 0 0 2px #3598DC
      -webkit-appearance none

    &::selection
      background #FFFFFF

  &__error-message
    display block
    margin-top 4px
    font-size 12px
    line-height 16px
    color #E7505A
</style>
